// Lightweight error-reporting seam.
//
// Today this logs structured errors to the console and, when a collector URL
// is configured, ships them to a backend endpoint. Swapping in a vendor
// (Sentry / Datadog / OpenTelemetry) later means changing ONLY this file —
// every error boundary, route `error.tsx`, and global handler already routes
// through `reportError`, so there is no retrofitting of call sites.
//
// By default (no NEXT_PUBLIC_ERROR_REPORTING_URL set) it is console-only, so we
// never emit failing network requests against a route that may not exist yet.

export interface ErrorContext {
  /** Where the error was caught, e.g. "react-boundary" | "global-error" | "app-error" | "feature:editor" | "unhandledrejection" | "window.onerror". */
  scope?: string;
  /** Feature name when caught by a FeatureBoundary. */
  feature?: string;
  /** React component stack, when available. */
  componentStack?: string | null;
  /** Next.js error digest (server-component errors). */
  digest?: string;
  [key: string]: unknown;
}

// Set to a collector endpoint to enable network reporting. Unset → console-only.
const REPORTING_URL = process.env.NEXT_PUBLIC_ERROR_REPORTING_URL;

function serializeError(error: unknown): { name?: string; message: string; stack?: string } {
  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack };
  }
  if (typeof error === "string") return { message: error };
  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: String(error) };
  }
}

/**
 * Report an error to the observability backend. Safe to call from anywhere —
 * it never throws and never blocks. Always logs; ships over the network only
 * when a collector URL is configured.
 */
export function reportError(error: unknown, context: ErrorContext = {}): void {
  const payload = {
    ...serializeError(error),
    ...context,
    url: typeof window !== "undefined" ? window.location.href : undefined,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    ts: new Date().toISOString(),
  };

  // Always log. Visible in dev; in prod it lands in the user's console for
  // support/repro until a vendor is wired into this function.
  console.error("[reportError]", payload);

  if (!REPORTING_URL || typeof window === "undefined") return;

  // Fire-and-forget. sendBeacon survives an imminent page reload (the common
  // case right after a crash); fall back to keepalive fetch otherwise.
  try {
    const body = JSON.stringify(payload);
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(REPORTING_URL, body);
    } else {
      void fetch(REPORTING_URL, {
        method: "POST",
        body,
        headers: { "Content-Type": "application/json" },
        keepalive: true,
      }).catch(() => {
        /* swallow — reporting must never surface its own failure */
      });
    }
  } catch {
    /* never throw from the reporter */
  }
}

let handlersInstalled = false;

/**
 * Install global handlers for the error classes React error boundaries cannot
 * catch: unhandled promise rejections and errors thrown in event handlers /
 * timers (which surface on `window.onerror`). Idempotent; call once on mount.
 */
export function installGlobalHandlers(): void {
  if (handlersInstalled || typeof window === "undefined") return;
  handlersInstalled = true;

  window.addEventListener("unhandledrejection", (event) => {
    reportError(event.reason ?? "Unhandled promise rejection", { scope: "unhandledrejection" });
  });

  window.addEventListener("error", (event) => {
    // Resource-load failures (img/script 404s) fire `error` with no `error`
    // object — ignore them here; they are not exceptions.
    if (!event.error) return;
    reportError(event.error, { scope: "window.onerror" });
  });
}
